import { Component, EventEmitter, Output, input, model } from '@angular/core';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ComoService } from '@core/services/como/como.service';
import { UserService } from '@core/services/user/user.service';
import { tap } from 'rxjs';
import { FidelityType } from '../fidelity-type.enum';
import { ZerosixService } from '@core/services/zerosix/zerosix.service';
import { ContentBuilderFieldColors } from '@shared/interfaces/content-builder.interface';
import { ZerosixError } from '@core/models/zerosix.model';
import { ZEROSIX_ERRORS } from '@config/values/zerosix-errors.values';

@Component({
  selector: 'app-kiosk-register-step',
  template: `
    <div class="flex items-center justify-between">
      <h1
        class="pb-5 font-brand text-28px font-bold text-neutral-800"
        translate="como.link-account.header"
      ></h1>
      <app-rounded-icon-button
        icon="icon-cross"
        (onClick)="onClose.emit()"
      ></app-rounded-icon-button>
    </div>

    <form>
      <label
        for="phone"
        class="mb-1 block font-base text-sm font-normal leading-5 text-neutral-600"
        translate="signup.phone"
      ></label>
      <label
        for="comoKioskRegistrationPhoneInput"
        class="mb-2 block font-base text-xs font-normal text-neutral-500"
        translate="como.help-phone-format"
      >
      </label>

      <app-kiosk-input-country-phone-atom
        id="phone"
        [phone]="phone()"
        [useLocalFormat]="useLocalFormat()"
        [defaultRegionCode]="defaultRegionCode()"
        (updatePhone)="phone.set($any($event))"
        (keydown)="handleKeyDown($event)"
      />

      <div class="hidden kiosk:block">
        <app-numeric-keypad-atom
          (numberClick)="onKeypadNumberClick($event)"
          (deleteClick)="onKeypadDeleteClick()"
        ></app-numeric-keypad-atom>
      </div>

      <div
        class="mt-2 font-base text-xs font-normal text-error-700"
        *ngIf="errorMessage"
        [translate]="errorMessage"
      ></div>

      <div class="mt-10 flex flex-col">
        <app-button-atom
          size="L"
          [label]="fidelityType() + '.validate' | translate"
          [iconUrl]="isValidating ? 'assets/images/spinner.svg' : undefined"
          [disabled]="isValidating || !phone()"
          [overrideTextColor]="accentColor()?.font"
          [overrideBgColor]="accentColor()?.background"
          type="primary"
          [full]="true"
          (click)="onRegister()"
        ></app-button-atom>
      </div>
    </form>
  `,
})
export class KioskRegisterStepComponent extends DestroyerBase {
  @Output() onComplete = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();
  @Output() goToKioskRegisterConfirmationStep = new EventEmitter<void>();

  readonly phone = model('');
  readonly useLocalFormat = input<boolean>(false);

  readonly accentColor = input<ContentBuilderFieldColors>();
  readonly fidelityType = input.required<FidelityType>();
  readonly defaultRegionCode = input<string>('fr');

  isValidating = false;
  errorMessage?: string;

  kioskParameters = this.settingsSelector.kiosk;

  constructor(
    private readonly zerosixService: ZerosixService,
    private readonly comoService: ComoService,
    private readonly settingsSelector: SettingsStoreSelector,
    private readonly userService: UserService,
  ) {
    super();
  }

  public onRegister(): void {
    switch (this.fidelityType()) {
      case FidelityType.COMO:
        this.registerComo();
        break;
      case FidelityType.ZEROSIX:
        this.registerZerosix();
        break;
      default:
        break;
    }
  }

  private registerComo(): void {
    this.comoService
      .createComoUser$(this.phone())
      .pipe(
        tap(user => this.userService.setUser(user)),
        tap(() => this.goToKioskRegisterConfirmationStep.next()),
      )
      .subscribe({
        error: error => this.handleErrorResponse(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  private registerZerosix(): void {
    this.zerosixService
      .createZerosixUser$(this.phone())
      .pipe(
        tap(user => this.userService.setUser(user)),
        tap(() => this.goToKioskRegisterConfirmationStep.next()),
      )
      .subscribe({
        error: error => this.handleErrorResponse(error.error),
        complete: () => (this.isValidating = false),
      });
  }

  private handleErrorResponse(error: ZerosixError): void {
    this.isValidating = false;

    if (error.class === 'ZerosixException') {
      this.errorMessage = 'zerosix.link-account.errors.account-not-found';
      this.errorMessage = ZEROSIX_ERRORS[error.code];
      return;
    }
    this.errorMessage = 'errors.generic-error';
  }

  onKeypadNumberClick(number: number): void {
    const currentValue = this.phone() || '';
    this.phone.set(currentValue + number.toString());
  }

  onKeypadDeleteClick(): void {
    const currentValue = this.phone();
    this.phone.set(currentValue.slice(0, -1)); // Supprime le dernier caractère
  }

  public handleKeyDown(event: KeyboardEvent): void {
    // Prevent default action for Enter key to avoid triggering the first button
    if (event.key === 'Enter') {
      event.preventDefault();
      // Optionally trigger form submission if the form is valid
      if (this.phone()) {
        this.onRegister();
      }
    }
  }
}
